'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Notification } from '@packages/shared';
import { useConfig, useCookies, useLoginState, useSession } from '@packages/utilities';
import { hasBeenClosed } from './helpers/closed';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { NewsletterSheetAgnitas } from './NewsletterSheetAgnitas';
import { NewsletterSheetOptimizely } from './NewsletterSheetOptimizely';

/**
 * The Newsletter Sheet Container Component:
 * A dialog at the bottom of the screen that is used to subscribe to the newsletter.
 *
 * It also checks your subscription state and hides itself if the user is already subscribed.
 * If the confirmation is still pending, a notification is displayed to remind the user.
 *
 * State:
 * - `forceHidden`: Boolean state to force hide the sheet.
 * - `notification`: Object containing notification state with `show` and `message`.
 *
 * Returns:
 * - `null` if the sheet should be hidden.
 * - A `Box` component containing the notification and the appropriate newsletter sheet component.
 */
const NewsletterSheet = () => {
  const { nlTexts } = useNlSheetTexts();
  const intl = useIntl();
  const {
    forms: {
      apiAgnitasUrl: {
        values: { useDOI },
      },
      newsletterSheet: { enabled: sheetEnabled },
      useOptimizely,
    },
  } = useConfig();
  const { loading: loggingIn } = useSession();
  const { loggedIn } = useLoginState();
  const closed = hasBeenClosed();

  const [forceHidden, setForceHidden] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string | JSX.Element;
  }>({ show: false, message: '' });
  const { getCookies } = useCookies();
  const finalUseOptimizely = getCookies()['inspire.optimizely'] === 'true' || useOptimizely;

  /**
   * Hide the sheet during login process, if it has been closed,
   * or it should be hidden for a logged in customer
   * */
  const hideSheet = forceHidden || !sheetEnabled || loggingIn || closed;

  if (hideSheet) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Notification for pending DOI E-Mail - the component instead the addToast is needed to have a Component as message child */}
      <Notification
        open={notification.show}
        severity="warning"
        autoHideDuration={7000}
        onClose={() => setNotification({ ...notification, show: false })}
      >
        {notification.message}
      </Notification>

      {finalUseOptimizely ? (
        <NewsletterSheetOptimizely
          intl={intl}
          nlTexts={nlTexts}
          loggedIn={loggedIn}
          useDOI={useDOI}
          setNotification={setNotification}
          notification={notification}
          setForceHidden={setForceHidden}
        />
      ) : (
        <NewsletterSheetAgnitas
          intl={intl}
          nlTexts={nlTexts}
          loggedIn={loggedIn}
          useDOI={useDOI}
          setNotification={setNotification}
          notification={notification}
          setForceHidden={setForceHidden}
        />
      )}
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewsletterSheet;
